import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Chip, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cotizacionVentaService, { CotizacionVenta, CotizacionVentaDetalle } from '../../services/cotizacionVentaService';
import clienteService from '../../services/clienteService';
import ubicacionService from '../../services/ubicacionService';
import PDFViewer from '../../components/shared/PDFViewer';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { parseLocalDate, toLocalDateStringYMD } from '../../utils/formatters';

type CotizacionVentaUI = CotizacionVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type CotizacionVentaUIExt = CotizacionVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; dni?: string; idCompania?: string };

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
/** Fallback al guardar si no hay sucursal elegida. */
const DEFAULT_NRO_SUC = 1;
/** Cotización nueva: 0 para que el combo de sucursal tome la primera de la lista cuando aplique. */
const NRO_SUC_COTIZACION_NUEVA = 0;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') return '';
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) return 0;
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

const CotizacionesVenta: React.FC = () => {
  const navigate = useNavigate();
  const { cotizacionId } = useParams<{ cotizacionId?: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingCotizacion, setLoadingCotizacion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);

  const modo = cotizacionId ? (location.pathname.includes('/ver') ? 'ver' : 'editar') : 'crear';
  const [cotizacionVenta, setCotizacionVenta] = useState<CotizacionVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '10',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: NRO_SUC_COTIZACION_NUEVA,
    cuit: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const isEditMode = modo === 'editar';
  /** Hay ID en la URL (editar/ver): no sobrescribir cabecera con defaults del cliente (p. ej. vendedor). */
  const esCotizacionPersistente = Boolean(cotizacionId);
  const isReadOnlyByPath = modo === 'ver';
  const isReadOnlyByEstado = (cotizacionVenta.estado === 'Facturado' || cotizacionVenta.estado === 'Preparado') && isEditMode;
  const isReadOnly = isReadOnlyByPath || isReadOnlyByEstado;

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroCotizacionCreada, setNroCotizacionCreada] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const round2 = (n: number) => Math.round(n * 100) / 100;

  useEffect(() => {
    if (cotizacionId) {
      const cargarCotizacion = async () => {
        try {
          setLoadingCotizacion(true);
          setError(null);
          const id = parseInt(cotizacionId, 10);
          if (isNaN(id)) {
            setError('ID de cotizacion invalido');
            navigate('/ventas/cotizaciones');
            return;
          }

          const cotizacionCargada = await cotizacionVentaService.getCotizacionVenta(id);
          let ubicacionFormateada = (cotizacionCargada as any).ubicacion || '';
          const nroUbicacionOrigen = (cotizacionCargada as any).nroUbicacionOrigen || (cotizacionCargada as any).nroUbicacion;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = ubicacionEncontrada
                ? `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (cotizacionCargada as any).transporte || '';
          const nroExpreso = (cotizacionCargada as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = cotizacionCargada.detalles?.map((detalle) => {
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            const subtotal = detalle.subtotal || 0;
            return { ...detalle, precioUnitario: precioLista, precioLista, descuento: porcDesc, porcDesc, subtotal };
          }) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => sum + (d.subtotal || 0), 0);
          const fechaEmisionDate = cotizacionCargada.fechaEmision ? (cotizacionCargada.fechaEmision instanceof Date ? cotizacionCargada.fechaEmision : new Date(cotizacionCargada.fechaEmision)) : new Date();
          const horaEmisionDate = cotizacionCargada.horaEmision ? (cotizacionCargada.horaEmision instanceof Date ? cotizacionCargada.horaEmision : new Date(cotizacionCargada.horaEmision)) : new Date();

          const nroVendedorCargado = Number(
            (cotizacionCargada as any).nroVendedor ?? (cotizacionCargada as any).NroVendedor ?? 0,
          );

          setCotizacionVenta({
            ...cotizacionCargada,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: (cotizacionCargada as any).ordenDeCompra || (cotizacionCargada as any).ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (cotizacionCargada as any).formaDePago || '',
            nroSuc: (cotizacionCargada as any).nroSuc || DEFAULT_NRO_SUC,
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
            cuit: (cotizacionCargada as any).cuit || '',
            tipoIVA: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            dni: (cotizacionCargada as any).dni || '',
            idCompania: (cotizacionCargada as any).idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: cotizacionCargada.importeTotal && cotizacionCargada.importeTotal > 0 ? cotizacionCargada.importeTotal : round2(importeTotalCalculado),
          } as CotizacionVentaUIExt);

          setPosCliente({
            nroCliente: Number(cotizacionCargada.nroCliente || 0),
            nombre: cotizacionCargada.nombre || '',
            cuit: (cotizacionCargada as any).cuit || '',
            dni: (cotizacionCargada as any).dni || '',
            tipoIva: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
          });
          setLineasPOS(
            detallesConSubtotales.map((d, idx) => ({
              id: `${cotizacionCargada.nroFormulario || 'cot'}-${idx + 1}`,
              codigoArticulo: d.codigoArticulo || '',
              descripcionArticulo: d.descripcionArticulo || '',
              cantidad: Number(d.cantidad || 0),
              precioUnitario: Number(d.precioUnitario || d.precioLista || 0),
              descuento: Number(d.descuento || d.porcDesc || 0),
              subtotal: Number(d.subtotal || 0),
              um: d.um || '',
              notas: d.notas,
            })),
          );
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar la cotizacion';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/cotizaciones'), 2000);
        } finally {
          setLoadingCotizacion(false);
        }
      };
      cargarCotizacion();
    }
  }, [cotizacionId, navigate, enqueueSnackbar]);

  const handleClienteSelect = (cliente: any) => {
    const posCli = cliente as POSCliente;
    setPosCliente(posCli);
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    setCotizacionVenta((prev: CotizacionVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      nroVendedor:
        esCotizacionPersistente || isReadOnly
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      formaDePago: formaPagoSugerida,
      transporte: transporteSugerido,
      porcDesc: normalizarPorcentajeBonificacion(cliente.porcDesc ?? (cliente as any)?.porc_Desc ?? (cliente as any)?.porcdesc),
      porcDesc2: normalizarPorcentajeBonificacion(cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2 ?? (cliente as any)?.porcdesc2),
      porcDesc3: normalizarPorcentajeBonificacion(cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3 ?? (cliente as any)?.porcdesc3),
    }));
  };

  const handleAgregarProductoPOS = (item: SuggestionItem) => {
    setLineasPOS((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: item.precioUnitario,
        um: item.um,
        stockDisponible: item.stockDisponible,
      },
    ]);
  };

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => {
    setLineasPOS((prev) => prev.filter((l) => l.id !== id));
  };

  const handleDuplicarLinea = (id: string) => {
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setLineasPOS((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '' },
    ]);
  };

  useEffect(() => {
    const v = Number(cotizacionVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [cotizacionVenta.nroSuc]);

  /**
   * Solo en cotizaci?n NUEVA: hidratar defaults desde el maestro de clientes al ingresar nroCliente.
   * En editar/ver los datos de cabecera (incl. vendedor) vienen del GET; volver a llamar getCliente
   * provocaba condiciones de carrera y el vendedor del cliente pisaba el del comprobante.
   */
  useEffect(() => {
    if (esCotizacionPersistente || isReadOnly) {
      return undefined;
    }
    const value = cotizacionVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            setCotizacionVenta((prev) => {
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                nroVendedor:
                  Number((c as any).vendedor) > 0 ? Number((c as any).vendedor) : 0,
                formaDePago: formaPagoSugerida,
                transporte: transporteSugerido,
                porcDesc: normalizarPorcentajeBonificacion(
                  c.porcDesc ?? (c as any)?.porc_Desc ?? (c as any)?.porcdesc,
                ),
                porcDesc2: normalizarPorcentajeBonificacion(
                  c.porcDesc2 ?? (c as any)?.porc_Desc2 ?? (c as any)?.porcdesc2,
                ),
                porcDesc3: normalizarPorcentajeBonificacion(
                  c.porcDesc3 ?? (c as any)?.porc_Desc3 ?? (c as any)?.porcdesc3,
                ),
              } as CotizacionVentaUIExt;
            });
          } catch {
            /* ignore */
          }
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [cotizacionVenta.nroCliente, esCotizacionPersistente, isReadOnly]);

  useEffect(() => {
    const detalles = lineasPOS.map((l, idx): CotizacionVentaDetalle => ({
      nroIdformulario: cotizacionVenta.nroFormulario,
      nroItem: idx + 1,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      precioLista: l.precioUnitario,
      descuento: l.descuento,
      porcDesc: l.descuento,
      subtotal: l.subtotal,
      notas: l.notas,
      um: l.um,
    }));
    const importeTotal = detalles.reduce((acc, d) => acc + (d.subtotal || 0), 0);
    setCotizacionVenta((prev) => ({ ...prev, detalles, importeTotal: round2(importeTotal) }));
  }, [lineasPOS, cotizacionVenta.nroFormulario]);

  const subtotalNetoPOS = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1POS = (subtotalNetoPOS * (cotizacionVenta.porcDesc || 0)) / 100;
  const baseDesc2POS = subtotalNetoPOS - importeDesc1POS;
  const importeDesc2POS = (baseDesc2POS * (cotizacionVenta.porcDesc2 || 0)) / 100;
  const baseDesc3POS = baseDesc2POS - importeDesc2POS;
  const importeDesc3POS = (baseDesc3POS * (cotizacionVenta.porcDesc3 || 0)) / 100;
  const subtotalConDescPOS = baseDesc3POS - importeDesc3POS;
  const ivaPOS = Math.round(subtotalConDescPOS * 0.21 * 100) / 100;
  const totalPOS = subtotalConDescPOS + ivaPOS;

  const handlePorcDescChangePOS = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setCotizacionVenta((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  const handleSave = async () => {
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar una cotizacion en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!cotizacionVenta.nroCliente || cotizacionVenta.nroCliente <= 0) erroresValidacion.push('Debe seleccionar un cliente');
      if (!cotizacionVenta.fechaEmision) erroresValidacion.push('Debe ingresar la Fecha de Emision');
      const ubicacion = (cotizacionVenta as CotizacionVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') erroresValidacion.push('Debe seleccionar una Ubicacion');
      const formaDePago = (cotizacionVenta as CotizacionVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!cotizacionVenta.detalles || cotizacionVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la cotizacion');
      } else {
        cotizacionVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene c?digo de art?culo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...cotizacionVenta,
        nroSuc: cotizacionVenta.nroSuc || DEFAULT_NRO_SUC,
        ordenCompra: cotizacionVenta.ordenCompra || '',
        transporte: cotizacionVenta.transporte || '',
        ubicacion: cotizacionVenta.ubicacion || '',
        formaDePago: cotizacionVenta.formaDePago || '',
      } as any;

      let response: CotizacionVenta;
      let nroCotizacion: number | null = null;

      if (isEditMode && cotizacionId) {
        const id = parseInt(cotizacionId, 10);
        await cotizacionVentaService.updateCotizacionVenta(id, payload);
        nroCotizacion = id;
        enqueueSnackbar('Cotizacion actualizada exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/cotizaciones'), 1500);
        setLoading(false);
        return;
      } else {
        response = await cotizacionVentaService.createCotizacionVenta(payload);
        if (response?.nroFormulario) nroCotizacion = response.nroFormulario;
        else if ((response as any)?.formulario?.nroFormulario) nroCotizacion = (response as any).formulario.nroFormulario;
      }

      if (nroCotizacion && !isEditMode) {
        setNroCotizacionCreada(nroCotizacion);
        setSuccessDialogOpen(true);
        try {
          setLoadingPDF(true);
          const blob = await cotizacionVentaService.getCotizacionVentaPDF(nroCotizacion);
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
          setTimeout(() => setSuccessDialogOpen(false), 1500);
          enqueueSnackbar(`Cotizacion #${nroCotizacion} lista para imprimir`, { variant: 'success', autoHideDuration: 3000 });
        } catch (pdfError) {
          enqueueSnackbar('Error al generar el PDF de la cotizacion', { variant: 'warning' });
        } finally {
          setLoadingPDF(false);
        }
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
      } else {
        enqueueSnackbar('Cotizacion guardada exitosamente', { variant: 'success' });
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar la cotizacion';
      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        errorMessage = err.message;
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        errores.forEach((e: string) => { if (e.trim()) enqueueSnackbar(e.trim(), { variant: 'error', autoHideDuration: 6000 }); });
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
          else if (val) msgs.push(`${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          errorMessage = msgs.join('\n');
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
        }
      } else if (status === 400 || status === 500) {
        errorMessage = data?.message || errorMessage;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => setSuccessDialogOpen(false);

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setNroCotizacionCreada(null);
    navigate('/ventas/cotizaciones');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/ventas/cotizaciones'))}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Cotizaciones
        </Button>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="h5" fontWeight={700} color="primary.main">
          {isReadOnly
            ? `Ver Cotizacion #${cotizacionId}`
            : isEditMode
              ? `Editar Cotizacion #${cotizacionId}`
              : 'Nueva Cotizacion'}
        </Typography>
        <Chip
          label={isReadOnly ? 'SOLO LECTURA' : isEditMode ? 'EDICION' : 'NUEVA'}
          size="small"
          color={isReadOnly ? 'default' : 'primary'}
          variant="outlined"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loadingCotizacion ? (
        <Box sx={{ p: 2, textAlign: 'center' }}><Typography>Cargando cotizacion...</Typography></Box>
      ) : (
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Cliente</Typography>
            <FacturaPOSClientePanel
              cliente={posCliente}
              onClienteSelect={(cliente) => {
                if (!isReadOnly) handleClienteSelect(cliente);
              }}
            />
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Agregar articulo</Typography>
            <FacturaPOSSearchBar
              nroLista={obtenerCodigoListaCliente(posCliente)}
              disabled={!posCliente || isReadOnly}
              onAgregarProducto={handleAgregarProductoPOS}
            />
          </Box>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
              <Typography variant="overline" color="text.secondary">Articulos</Typography>
              {lineasPOS.length > 0 && (
                <Chip label={`${lineasPOS.length} renglon${lineasPOS.length > 1 ? 'es' : ''}`} size="small" />
              )}
            </Stack>
            <FacturaPOSLineasGrid
              lineas={lineasPOS}
              nroLista={obtenerCodigoListaCliente(posCliente)}
              readOnly={isReadOnly}
              onLineaChange={handleLineaChange}
              onLineaFilled={handleLineaFilled}
              onEliminarLinea={handleEliminarLinea}
              onDuplicarLinea={handleDuplicarLinea}
              onAgregarLineaVacia={handleAgregarLineaVacia}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 420 }, ml: { md: 'auto' } }}>
            <FacturaPOSTotalesPanel
              subtotalNeto={subtotalNetoPOS}
              porcDesc={cotizacionVenta.porcDesc || 0}
              porcDesc2={cotizacionVenta.porcDesc2 || 0}
              porcDesc3={cotizacionVenta.porcDesc3 || 0}
              onPorcDescChange={handlePorcDescChangePOS}
              iva={ivaPOS}
              total={totalPOS}
              observaciones={cotizacionVenta.descripcion ?? ''}
              onObservacionesChange={(v) =>
                setCotizacionVenta((prev) => ({ ...prev, descripcion: v }))
              }
              cliente={posCliente}
              fechaEmision={toLocalDateStringYMD(
                cotizacionVenta.fechaEmision instanceof Date
                  ? cotizacionVenta.fechaEmision
                  : new Date(cotizacionVenta.fechaEmision as string | number),
              )}
              onFechaEmisionChange={(ymd) =>
                setCotizacionVenta((prev) => ({ ...prev, fechaEmision: parseLocalDate(ymd) }))
              }
              nroVendedor={Number(cotizacionVenta.nroVendedor) || 0}
              onNroVendedorChange={(nro) =>
                setCotizacionVenta((prev) => ({ ...prev, nroVendedor: nro }))
              }
              formaDePago={cotizacionVenta.formaDePago || ''}
              onFormaDePagoChange={(v) => setCotizacionVenta((prev) => ({ ...prev, formaDePago: v }))}
              ordenCompra={cotizacionVenta.ordenCompra || ''}
              onOrdenCompraChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ordenCompra: v }))}
              transporte={cotizacionVenta.transporte || ''}
              onTransporteChange={(v) => setCotizacionVenta((prev) => ({ ...prev, transporte: v }))}
              ubicacion={cotizacionVenta.ubicacion || ''}
              onUbicacionChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ubicacion: v }))}
              saving={loading}
              onGuardar={() => handleSave()}
              cantLineas={lineasPOS.length}
              readOnly={isReadOnly}
              hideDraftButton
              saveLabel="Crear Cotización"
            />
          </Box>
        </Stack>
      )}

      <Dialog open={successDialogOpen} onClose={handleCloseSuccessDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, boxShadow: 6 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pb: 1.5, pt: 3, px: 3 }}>Cotizacion Creada</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Cotizacion creada exitosamente! Numero: {nroCotizacionCreada}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseSuccessDialog} variant="text" color="primary" sx={{ minWidth: 80, textTransform: 'none', fontWeight: 500 }} disabled={loadingPDF}>
            {loadingPDF ? 'Generando PDF...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && nroCotizacionCreada && (
        <PDFViewer pdfUrl={pdfUrl} onClose={handleClosePDF} title={`Cotizacion #${nroCotizacionCreada}`} documentId={`${nroCotizacionCreada}`} documentType="cotizacion" />
      )}
    </Box>
  );
};

export default CotizacionesVenta;
